import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera,
  Refresh,
  Business as BusinessIcon,
  LocationOn,
  Badge,
  AccessTime,
  AccountBalance,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { companyApi, companyLocationApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { STATIC_BASE_URL } from '../utils/runtimeUrls';
import {
  CompanyCurrency,
  CompanyProfileFormData,
  emptyCompanyProfileFormData,
  mapCompanyToCompanyProfileFormData,
  settingsCardSx,
} from '../settings/companySettingsShared';
import { COMPANY_SETTINGS_QUERY_KEY, useCompanySettingsQuery } from '../settings/useCompanySettingsQuery';
import { useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import { SettingsPageHeader } from '../settings/SettingsPageHeader';
import { LocationMapView } from './publicTemplates/LocationMapView';
import { LocationMapInlineEditor } from '../components/locations/LocationMapInlineEditor';
import { CompanyLocationUpsertRequest, GeocodeResult } from '../types/companyLocation';

interface PendingCompanyAddressGeo {
  latitude: number;
  longitude: number;
  plusCode?: string | null;
}

export const CompanyProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  const [isDirty, setIsDirty] = useState(false);
  const [settingsData, setSettingsData] = useState<CompanyProfileFormData>(
    emptyCompanyProfileFormData
  );
  const [pendingAddressGeo, setPendingAddressGeo] = useState<PendingCompanyAddressGeo | null>(null);

  const settingsQuery = useCompanySettingsQuery(companyId);
  const domainSettingsQuery = useDomainSettingsQuery(companyId);
  const locationsQuery = useQuery(
    ['companyLocations', companyId],
    () => companyLocationApi.getAll(false),
    { enabled: !!companyId }
  );
  const primaryLocation = locationsQuery.data?.find((location) => location.primaryCompanyAddress);

  useEffect(() => {
    if (!settingsQuery.data?.company) {
      return;
    }

    setSettingsData(mapCompanyToCompanyProfileFormData(settingsQuery.data.company));
    setIsDirty(false);
    setPendingAddressGeo(null);
  }, [settingsQuery.data]);

  const uploadLogoMutation = useMutation((file: File) => companyApi.uploadLogo(file), {
    onSuccess: () => {
      queryClient.invalidateQueries([COMPANY_SETTINGS_QUERY_KEY, companyId]);
      toast.success(t('settings.messages.logoUploaded'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('settings.messages.logoUploadFailed'));
    },
  });

  const updateSettingsMutation = useMutation(
    (data: CompanyProfileFormData) => companyApi.updateSettings(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([COMPANY_SETTINGS_QUERY_KEY, companyId]);
        toast.success(t('settings.messages.settingsUpdated'));
        setIsDirty(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('settings.messages.settingsUpdateFailed'));
      },
    }
  );

  // The company's address text lives on Company itself, but its map pin lives on its
  // primary CompanyLocation (the only place lat/lng is stored). Saving the profile pushes
  // the text fields through companyApi.updateSettings as usual, then - if the map pin
  // changed - pushes the coordinates straight to that primary location.
  const updatePrimaryLocationGeoMutation = useMutation(
    (vars: { id: string; data: CompanyLocationUpsertRequest }) =>
      companyLocationApi.update(vars.id, vars.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['companyLocations', companyId]);
        setPendingAddressGeo(null);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('locations.messages.updateFailed', { defaultValue: 'Failed to update location' })
        );
      },
    }
  );

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('fleet.validation.imageSize'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error(t('fleet.validation.imageType'));
      return;
    }

    uploadLogoMutation.mutate(file);
  };

  const handleInputChange = (field: keyof CompanyProfileFormData, value: string) => {
    setSettingsData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleCurrencyChange = (value: CompanyCurrency) => {
    setSettingsData((prev) => ({ ...prev, currency: value }));
    setIsDirty(true);
  };

  const currencyLocked = Boolean(settingsQuery.data?.currencyLocked);

  const requiredFieldLabels = useMemo(
    () => [
      t('fields.companyName'),
      t('fields.taxId'),
      t('fields.email'),
      t('fields.phoneNumber'),
      t('fields.website'),
      t('fields.streetAddress'),
      t('fields.city'),
      t('fields.country'),
    ],
    [t]
  );

  const handleAddressMapPickerConfirm = (result: GeocodeResult) => {
    setSettingsData((prev) => ({
      ...prev,
      streetAddress: result.address || prev.streetAddress,
      city: result.city || prev.city,
      state: result.state ?? prev.state,
      postalCode: result.postalCode ?? prev.postalCode,
      country: result.country || prev.country,
    }));
    setPendingAddressGeo({
      latitude: result.latitude,
      longitude: result.longitude,
      plusCode: result.plusCode ?? null,
    });
    setIsDirty(true);
  };

  const handleSaveSettings = async () => {
    const missingRequiredFields = [
      !settingsData.companyName.trim(),
      !settingsData.taxId.trim(),
      !settingsData.email.trim(),
      !settingsData.phoneNumber.trim(),
      !settingsData.website.trim(),
      !settingsData.streetAddress.trim(),
      !settingsData.city.trim(),
      !settingsData.country.trim(),
    ]
      .map((missing, index) => (missing ? requiredFieldLabels[index] : null))
      .filter((value): value is string => Boolean(value));

    if (missingRequiredFields.length > 0) {
      toast.error(
        `Please complete the required company fields: ${missingRequiredFields.join(', ')}`
      );
      return;
    }

    try {
      await updateSettingsMutation.mutateAsync(settingsData);
    } catch {
      // Error already surfaced by updateSettingsMutation's onError toast.
      return;
    }

    if (pendingAddressGeo && primaryLocation) {
      updatePrimaryLocationGeoMutation.mutate({
        id: primaryLocation.id.toString(),
        data: {
          name: primaryLocation.name,
          address: settingsData.streetAddress,
          city: settingsData.city,
          state: settingsData.state,
          postalCode: settingsData.postalCode,
          country: settingsData.country,
          latitude: pendingAddressGeo.latitude,
          longitude: pendingAddressGeo.longitude,
          plusCode: pendingAddressGeo.plusCode ?? primaryLocation.plusCode,
          active: primaryLocation.active,
          servesAirport: primaryLocation.servesAirport,
          servesPort: primaryLocation.servesPort,
        },
      });
    }
  };

  const company = settingsQuery.data?.company;
  const logoUrl = company?.logoUrl
    ? company.logoUrl.startsWith('http')
      ? company.logoUrl
      : `${STATIC_BASE_URL}${company.logoUrl}`
    : null;

  const companyInitials = useMemo(
    () =>
      (settingsData.companyName || user?.company?.name || 'RM')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join(''),
    [settingsData.companyName, user?.company?.name]
  );

  // Kept short on purpose: street + city is enough to identify the address, and state/
  // postal code/country just make this summary unwieldy (Albania barely uses either).
  const locationSummary = [settingsData.streetAddress, settingsData.city]
    .filter(Boolean)
    .join(', ');

  if (settingsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <SettingsPageHeader
        icon={<BusinessIcon color="primary" />}
        title={t('settings.companyTitle', { defaultValue: 'Company' })}
        subtitle={t('settings.companySubtitle', {
          defaultValue:
            'Manage your business identity, legal details, address, branding, banking, and public contact information.',
        })}
        actions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: { xs: '100%', lg: 'auto' } }}
          >
            <Tooltip title={t('common.refresh')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => settingsQuery.refetch()}
                  sx={{ minWidth: 150 }}
                >
                  {t('common.refresh')}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={
                updateSettingsMutation.isLoading || updatePrimaryLocationGeoMutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />
              }
              onClick={handleSaveSettings}
              disabled={!isDirty || updateSettingsMutation.isLoading || updatePrimaryLocationGeoMutation.isLoading}
              sx={{ minWidth: 180 }}
            >
              {t('common.saveChanges')}
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <Paper sx={settingsCardSx}>
              <Typography variant="h6" gutterBottom>
                {t('settings.logo')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('settings.brandingHint', {
                  defaultValue:
                    'This logo and company summary appear across internal admin pages and customer-facing documents.',
                })}
              </Typography>

              <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center' }}>
                {logoUrl ? (
                  <Avatar
                    src={logoUrl}
                    alt={settingsData.companyName || t('settings.logo')}
                    sx={{ width: 160, height: 160, borderRadius: 4, bgcolor: 'grey.100' }}
                    variant="rounded"
                  />
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 160,
                      height: 160,
                      borderRadius: 4,
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      fontSize: '3rem',
                      fontWeight: 700,
                    }}
                  >
                    {companyInitials || <BusinessIcon fontSize="large" />}
                  </Avatar>
                )}

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {settingsData.companyName || t('fields.companyName')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settingsData.email ||
                      t('settings.publicContact', {
                        defaultValue: 'Public contact information will appear here.',
                      })}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  disabled={uploadLogoMutation.isLoading}
                >
                  {uploadLogoMutation.isLoading ? t('common.uploading') : t('settings.uploadLogo')}
                  <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                </Button>

                <Typography variant="caption" color="text.secondary">
                  {t('settings.logoHint')}
                </Typography>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('settings.summaryTitle', { defaultValue: 'At a glance' })}
                  </Typography>
                  <Typography variant="body2">
                    {locationSummary ||
                      t('settings.addressEmpty', {
                        defaultValue:
                          'Add your company address so invoices and company documents look complete.',
                      })}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="h6">
                    {t('settings.bankingSection', { defaultValue: 'Banking Details' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.bankingHint', {
                      defaultValue:
                        'Use these details on invoices, payment instructions, and other company documents.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label={t('fields.bankName', { defaultValue: 'Bank Name' })}
                  value={settingsData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('fields.bankAccountNumber', {
                    defaultValue: 'IBAN / Account Number',
                  })}
                  value={settingsData.bankAccountNumber}
                  onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('fields.bankRoutingNumber', {
                    defaultValue: 'SWIFT / BIC / Routing',
                  })}
                  value={settingsData.bankRoutingNumber}
                  onChange={(e) => handleInputChange('bankRoutingNumber', e.target.value)}
                />
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <BusinessIcon color="primary" />
                <Box>
                  <Typography variant="h6">{t('settings.companyInformation')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.companyInfoHint', {
                      defaultValue:
                        'These details define your business identity on invoices, documents, and admin screens.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.companyName')}
                    value={settingsData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.website')}
                    value={settingsData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.email')}
                    type="email"
                    value={settingsData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.phoneNumber')}
                    type="tel"
                    value={settingsData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.taxId')}
                    value={settingsData.taxId}
                    onChange={(e) => handleInputChange('taxId', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Tooltip
                    title={
                      currencyLocked
                        ? t('settings.currencyLockedHint', {
                            defaultValue:
                              'Currency can no longer be changed because this company already has reservations, invoices, payments, expenses, or maintenance records.',
                          })
                        : t('settings.currencyHint', {
                            defaultValue:
                              'This is the single currency used everywhere for this company: rentals, invoices, reports, maintenance, and printouts. Choose carefully - it can only be changed before the first transaction is recorded.',
                          })
                    }
                  >
                    <TextField
                      select
                      fullWidth
                      label={t('fields.currency', { defaultValue: 'Currency' })}
                      value={settingsData.currency}
                      onChange={(e) => handleCurrencyChange(e.target.value as CompanyCurrency)}
                      disabled={currencyLocked}
                    >
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="LEK">LEK (Lek)</MenuItem>
                    </TextField>
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('fields.description')}
                    value={settingsData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    multiline
                    minRows={4}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <LocationOn color="primary" />
                <Box>
                  <Typography variant="h6">
                    {t('settings.addressSection', { defaultValue: 'Business Address' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.addressHint', {
                      defaultValue:
                        'Use your official business address so documents, billing, and customer communication stay accurate.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.streetAddress')}
                    value={settingsData.streetAddress}
                    onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.city')}
                    value={settingsData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('fields.state')}
                    value={settingsData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('fields.postalCode')}
                    value={settingsData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label={t('fields.country')}
                    value={settingsData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('settings.addressMapTitle', { defaultValue: 'Pin your address on the map' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.addressMapHint', {
                  defaultValue:
                    'Click the map or drag the pin to your exact address, then resolve and apply it. This sets the coordinates used for your public page and this main location.',
                })}
              </Typography>

              {locationsQuery.isLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <LocationMapInlineEditor
                  latitude={pendingAddressGeo?.latitude ?? primaryLocation?.latitude}
                  longitude={pendingAddressGeo?.longitude ?? primaryLocation?.longitude}
                  apiKey={domainSettingsQuery.data?.googleMapsApiKey}
                  onApply={handleAddressMapPickerConfirm}
                />
              )}
            </Paper>

            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Badge color="primary" />
                <Box>
                  <Typography variant="h6">
                    {t('settings.contactSection', { defaultValue: 'Contacts & Operations' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.contactHint', {
                      defaultValue:
                        'Keep the right people, opening hours, and emergency numbers attached to the company profile.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('fields.contactPersonName')}
                    value={settingsData.contactPersonName}
                    onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('fields.contactPersonTitle')}
                    value={settingsData.contactPersonTitle}
                    onChange={(e) => handleInputChange('contactPersonTitle', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('fields.emergencyContact')}
                    value={settingsData.emergencyContact}
                    onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('fields.operatingHoursStart')}
                    value={settingsData.operatingHoursStart}
                    onChange={(e) => handleInputChange('operatingHoursStart', e.target.value)}
                    type="time"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('fields.operatingHoursEnd')}
                    value={settingsData.operatingHoursEnd}
                    onChange={(e) => handleInputChange('operatingHoursEnd', e.target.value)}
                    type="time"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('fields.operatingDays')}
                    value={settingsData.operatingDays}
                    onChange={(e) => handleInputChange('operatingDays', e.target.value)}
                    placeholder={t('placeholders.operatingDays')}
                    InputProps={{
                      startAdornment: <AccessTime sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={settingsCardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <PublicIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    {t('settings.publicPageSection', { defaultValue: 'Public Page' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.publicPageHint', {
                      defaultValue:
                        'Shown to customers on your public search page. The description above is used as your About text; add a short tagline and links so visitors can find and message you.',
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('fields.heroTagline', { defaultValue: 'Hero Tagline' })}
                    value={settingsData.heroTagline}
                    onChange={(e) => handleInputChange('heroTagline', e.target.value)}
                    placeholder={t('placeholders.heroTagline', {
                      defaultValue: 'e.g. The security and pleasure of driving a car as if it was yours',
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('fields.whatsappNumber', { defaultValue: 'WhatsApp Number' })}
                    value={settingsData.whatsappNumber}
                    onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                    placeholder="+355 69 000 0000"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('fields.instagramUrl', { defaultValue: 'Instagram URL' })}
                    value={settingsData.instagramUrl}
                    onChange={(e) => handleInputChange('instagramUrl', e.target.value)}
                    placeholder="https://instagram.com/yourcompany"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('fields.facebookUrl', { defaultValue: 'Facebook URL' })}
                    value={settingsData.facebookUrl}
                    onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                    placeholder="https://facebook.com/yourcompany"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('settings.publicPageMapPreviewTitle', { defaultValue: 'Location on your public page' })}
                  </Typography>
                  {primaryLocation?.latitude != null && primaryLocation?.longitude != null ? (
                    <Box sx={{ height: 260, borderRadius: 2, overflow: 'hidden' }}>
                      <LocationMapView
                        apiKey={domainSettingsQuery.data?.googleMapsApiKey || undefined}
                        latitude={primaryLocation.latitude}
                        longitude={primaryLocation.longitude}
                      />
                    </Box>
                  ) : (
                    <Alert severity="info">
                      {t('settings.publicPageMapPreviewMissing', {
                        defaultValue:
                          'No map pin yet. Open Locations, select your primary address, and drop a pin on the map to show it here and on your public page.',
                      })}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={
                  updateSettingsMutation.isLoading || updatePrimaryLocationGeoMutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />
                }
                onClick={handleSaveSettings}
                disabled={!isDirty || updateSettingsMutation.isLoading || updatePrimaryLocationGeoMutation.isLoading}
              >
                {t('settings.saveSettings')}
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {settingsQuery.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t('common.error', { defaultValue: 'Error' })}
        </Alert>
      )}
    </Container>
  );
};
